import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import { useSelector } from 'react-redux'
import moment from 'moment'
import numeral from 'numeral'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import ReactImageMagnify from 'react-image-magnify'
// import { JSONTree } from 'react-json-tree'

import classes from './styles.module.scss'

import getUniqueArrayByKey from '../../../../helpers/getUniqueArrayByKey'
import { extractDataFromInvoice, createPlaceExpense, updatePlaceExpense, fetchExpenseFile } from '../../../../api'

import UploadIcon from '../../../Icons/UploadIcon'
import WandIcon from '../../../Icons/WandIcon'
import RefreshIcon from '../../../Icons/RefreshIcon'
import AppCheckbox from '../../../Core/AppCheckbox'
import Button from '../../Button/Button'
import Input from '../../Input/Input'
import Label from '../../InputLabel'
import InputSelect from '../../InputSelect'
import CreateSupplier from '../../../Core/Dialog/CreateSupplier'
import CreateMaterial from '../../../Core/Dialog/CreateMaterial'
import TrashIcon from '../../../Icons/TrashIcon'

const CheckIcon = (props) => {
  return (
    <svg
    xmlns="http://www.w3.org/2000/svg"
    width={10}
    height={10}
    fill="none"
    {...props}
  >
    <g clipPath="url(#a)">
      <path
        fill="#10DF55"
        d="M9.822 4.998c0 2.753-2.2 4.997-4.911 4.997C2.205 9.995 0 7.751 0 4.998 0 2.239 2.205 0 4.91 0c2.712 0 4.912 2.24 4.912 4.998ZM6.408 3.052 4.352 6.414 3.375 5.13c-.12-.162-.226-.206-.366-.206a.392.392 0 0 0-.385.402c0 .113.043.22.116.318l1.208 1.51c.125.17.26.24.424.24a.462.462 0 0 0 .404-.24L7.04 3.527c.058-.103.12-.216.12-.324 0-.23-.197-.377-.409-.377-.125 0-.25.078-.342.225Z"
      />
    </g>
    <defs>
      <clipPath id="a">
        <path fill="#fff" d="M0 0h10v10H0z" />
      </clipPath>
    </defs>
  </svg>
  )
}

const AttentionIcon = (props) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={11}
      height={10}
      fill="none"
      {...props}
    >
      <g clipPath="url(#a)">
        <path
          fill="#FBAF0B"
          d="m6.606.717 4.002 7.075c.129.23.201.486.201.732 0 .8-.531 1.418-1.408 1.418H1.408C.53 9.942 0 9.325 0 8.524c0-.246.062-.497.201-.732L4.203.717a1.363 1.363 0 0 1 2.403 0ZM4.832 7.64c0 .309.268.56.578.56a.57.57 0 0 0 .572-.56.569.569 0 0 0-.572-.565.573.573 0 0 0-.578.565Zm.088-4.474.067 2.847c.005.277.155.434.423.434.252 0 .402-.152.407-.434l.077-2.842c.006-.277-.21-.481-.49-.481-.288 0-.49.199-.484.476Z"
        />
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M0 0h11v10H0z" />
        </clipPath>
      </defs>
    </svg>
  )
}

const parseNumber = (str) => {
  if (!str) return NaN;
  let cleaned = str.replace(/[^\d.,-]/g, "");
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  let decimalSeparator = null;

  if (lastComma > lastDot) decimalSeparator = ",";
  else decimalSeparator = ".";

  if (decimalSeparator) {
    const parts = cleaned.split(decimalSeparator);
    const integer = parts[0].replace(/[.,]/g, "");
    const decimal = parts[1] || "";
    return Number(`${integer}.${decimal}`);
  }

  return Number(cleaned.replace(/[^\d-]/g, ""));
}

const convertPdfToImage = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()
    fileReader.onload = function() {
      const typedarray = new Uint8Array(this.result)

      window.pdfjsLib.getDocument(typedarray).promise.then(pdf => {
        pdf.getPage(1).then(page => {
          const viewport = page.getViewport({ scale: 2 })
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          canvas.height = viewport.height
          canvas.width = viewport.width

          page.render({
            canvasContext: context,
            viewport: viewport
          }).promise.then(() => {
            const res = canvas.toDataURL('image/png')
            resolve(res)
          })
        })
      })
    }
    fileReader.readAsArrayBuffer(file)
  })
}

const convertPdfBase64ToImage = async (pdfBase64) => {
  const base64Content = String(pdfBase64 || '').replace(/^data:application\/pdf;base64,/, '')
  const binaryString = window.atob(base64Content)
  const len = binaryString.length
  const bytes = new Uint8Array(len)

  for (let i = 0; i < len; i += 1) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  const pdf = await window.pdfjsLib.getDocument(bytes).promise
  const page = await pdf.getPage(1)
  const viewport = page.getViewport({ scale: 2 })
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  canvas.height = viewport.height
  canvas.width = viewport.width

  await page.render({
    canvasContext: context,
    viewport
  }).promise

  return canvas.toDataURL('image/png')
}

const convertWebpToPng = (file) => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height

      const context = canvas.getContext('2d')
      context.drawImage(image, 0, 0)

      canvas.toBlob((blob) => {
        URL.revokeObjectURL(objectUrl)

        if (!blob) {
          reject(new Error('Failed to convert WEBP image'))
          return
        }

        const fileNameWithoutExt = file.name.replace(/\.[^.]+$/, '')
        resolve(new File([blob], `${fileNameWithoutExt}.png`, { type: 'image/png' }))
      }, 'image/png')
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load WEBP image'))
    }

    image.src = objectUrl
  })
}

const CreateExpense = forwardRef(({ companyId, placeId, currencies = [], customers = [], expensesTypes = [], suppliers = [], materials = [], onCreateSupplier, onCreateMaterial }, ref) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company?.companyInfo) || {}
  const dateInputFormat = 'YYYY-MM-DD'

  const [open, setOpen] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [errors, setErrors] = useState({})
  const [form, setForm] = useState({
    detectAutomatically: true,
    file: null,
    amount: '',
    currency_code: companyInfo.currency || '',
    customer_id: '',
    vendor_name: '',
    supplier_id: '',
    invoiceNumber: '',
    invoiceDate: moment().format(dateInputFormat),
    expensesType: '',
    notes: '',
    materials: [{
      material_id: '',
      quantity: '1',
      price: '0'
    }]
  })
  const [touchedFields, setTouchedFields] = useState({})
  const [detectionConfidence, setDetectionConfidence] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isCreateSupplierOpen, setIsCreateSupplierOpen] = useState(false)
  const [isCreateMaterialOpen, setIsCreateMaterialOpen] = useState(false)

  const promissesRef = useRef({ resolve: null, reject: null })
  const fileInputRef = useRef(null)
  const amountRef = useRef(0)

  const formMaterials = form.materials.filter(m => Boolean(m.material_id) && Boolean(m.quantity) && Boolean(m.price))
  const materialsAmount = formMaterials.reduce((total, material) => {
    const quantity = parseFloat(material.quantity) || 0
    const price = parseFloat(material.price) || 0
    return total + quantity * price
  }, 0)

  const currenciesOptions = getUniqueArrayByKey(currencies, 'code').map(curr => ({
    id: curr.code,
    name: `${curr.code} ${curr.name ? `(${curr.name})` : ''}`
  }))

  const merchantsOptions = customers?.map(customer => ({
    id: customer.id,
    name: customer.display_name,
  }))

  const expenseTypesOptions = expensesTypes?.map(type => ({
    id: type.id,
    name: type.name
  }))

  const suppliersOptions = suppliers?.map(supplier => ({
    id: supplier.id,
    name: supplier.name,
  }))

  useImperativeHandle(ref, () => ({
    show: async (data) => {
      return new Promise((resolve, reject) => {
        promissesRef.current = { resolve, reject }
        setOpen(true)
        if (data) {
          const materialsCost = data.materials?.reduce((total, item) => {
            const quantity = Number(item.pivot.quantity) || 0
            const price = Number(item.pivot.price) || 0
            return total + quantity * price
          }, 0)
          amountRef.current = (data.amount || 0) - materialsCost
          setForm({
            id: data.id,
            detectAutomatically: data.auto_detection || false,
            file: data.file,
            amount: amountRef.current,
            currency_code: data.currency_code || companyInfo.currency || '',
            customer_id: data.customer_id || '',
            supplier_id: data.supplier_id || '',
            vendor_name: data.vendor_name || '',
            invoiceNumber: data.invoice_number || '',
            invoiceDate: data.invoice_date ? moment(data.invoice_date).format(dateInputFormat) : moment().format(dateInputFormat),
            expensesType: data.expenses_types_id || '',
            notes: data.notes || '',
            materials: data.materials?.length ? data.materials.map(item => ({
              material_id: item.id,
              quantity: Number(item.pivot.quantity),
              price: Number(item.pivot.price),
            })) : [{
              material_id: '',
              quantity: '1',
              price: '0'
            }]
          })
          if (data.file) {
            if (String(data.file).toLowerCase().endsWith('.pdf')) {
              fetchExpenseFile(companyId, placeId, data.id)
                .then(base64 => convertPdfBase64ToImage(base64))
                .then(imageBase64 => {
                  setPreviewUrl(imageBase64)
                })
              return
            }

            setPreviewUrl(data.file)
          }
        }
      })
    },
    close: () => {
      setOpen(false)
    }
  }))

  useEffect(() => {
    if (materialsAmount) {
      setForm(prev => ({
        ...prev,
        amount: Number(amountRef.current) + materialsAmount
      }))
    }
  }, [materialsAmount])

  useEffect(() => {
    if (form.detectAutomatically && form.file && !form.id) {
      analyzeFile()
    }
  }, [form.detectAutomatically, form.file, form.id])

  const handleClose = (success) => {
    if (promissesRef.current.resolve) {
      promissesRef.current.resolve({ success, data: success ? form : null })
    }
    setOpen(false)
    resetForm()
  }

  const resetForm = () => {
    amountRef.current = 0
    setForm({
      detectAutomatically: true,
      file: null,
      amount: '',
      currency_code: companyInfo.currency || '',
      customer_id: '',
      supplier_id: '',
      invoiceNumber: '',
      invoiceDate: moment().format(dateInputFormat),
      expensesType: '',
      notes: '',
      materials: [{
        material_id: '',
        quantity: '1',
        price: '0'
      }]
    })
    setTouchedFields({})
    setErrors({})
    setPreviewUrl('')
    setDetectionConfidence(0)
    setLoading(false)
    // setRawDetectedData(null)
  }

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target
    const inputValue = type === 'checkbox' ? checked : value

    setForm(prev => ({
      ...prev,
      [name]: inputValue
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }))
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }))
  }

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      let file = selectedFile

      if (selectedFile.type === 'image/webp') {
        try {
          file = await convertWebpToPng(selectedFile)
        } catch (error) {
          setErrors(prev => ({
            ...prev,
            file: true
          }))
          return
        }
      }

      setForm(prev => ({
        ...prev,
        file: file
      }))
      if (errors.file) {
        setErrors(prev => ({
          ...prev,
          file: false
        }))
      }
      if (file.type === 'application/pdf') {
        convertPdfToImage(file).then(res => {
          setPreviewUrl(res)
        })
        return
      }
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const analyzeFile = async () => {
    setAnalyzing(true)
    const res = await extractDataFromInvoice(companyId, placeId, form.file)
    if (res) {
      handleExtractedData(res)
    }
    setAnalyzing(false)
  }

  const handleExtractedData = (data) => {
    if (!data) {
      return
    }
    const updates = {}
    if (data.amount) {
      updates.amount =parseNumber(data.amount)
    }
    if (data.currency) {
      updates.currency_code = data.currency
    }
    if (data.invoice_number) {
      updates.invoiceNumber = data.invoice_number
    }
    if (data.date) {
      updates.invoiceDate = moment(data.date).format(dateInputFormat)
    }
    if (data.notes) {
      updates.notes = data.notes
    }
    if (data.vendor_name) {
      updates.vendor_name = data.vendor_name
    }
    setForm(prev => ({
      ...prev,
      ...updates
    }))
    setDetectionConfidence(data.detection_confidence || 0)
    // setRawDetectedData(data.raw)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const file = event.dataTransfer.files?.[0]
    const acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf']
    if (file && acceptedTypes.includes(file.type)) {
      setForm(prev => ({
        ...prev,
        file: file
      }))
      if (errors.file) {
        setErrors(prev => ({
          ...prev,
          file: false
        }))
      }
    }
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleExited = () => {
    resetForm()
  }

  const onClose = () => {
    handleExited()
    handleClose(false)
  }

  const validateForm = () => {
    const amountValue = numeral(form.amount).value()
    const nextErrors = {
      amount: !amountValue || amountValue <= 0,
      currency_code: !String(form.currency_code || '').trim(),
      invoiceDate: !String(form.invoiceDate || '').trim(),
      supplier_id: !String(form.supplier_id || '').trim(),
      // file: !form.file && !form.id
    }

    const hasErrors = Object.values(nextErrors).some(Boolean)
    setErrors(nextErrors)

    return !hasErrors
  }

  const handleSubmit = async () => {
    if (loading || !validateForm()) {
      return
    }

    const post = new FormData()
    post.append('amount', form.amount || '')
    post.append('currency_code', form.currency_code || '')
    post.append('customer_id', form.customer_id || '')
    post.append('vendor_name', form.vendor_name || '')
    post.append('supplier_id', form.supplier_id || '')
    post.append('invoice_number', form.invoiceNumber || '')
    post.append('invoice_date', form.invoiceDate || '')
    post.append('expenses_types_id', form.expensesType || '')
    post.append('notes', form.notes || '')
    post.append('auto_detection', form.detectAutomatically ? '1' : '0')
    post.append('status', 'pending')

    formMaterials.forEach((material, index) => {
      post.append(`materials[${index}][material_id]`, material.material_id || '')
      post.append(`materials[${index}][quantity]`, material.quantity || '')
      post.append(`materials[${index}][price]`, material.price || '')
    })

    if (form.file) {
      post.append('file', form.file)
    }
    setLoading(true)
    if (form.id) {
      await updatePlaceExpense(companyId, placeId, form.id, post)
    } else {
      await createPlaceExpense(companyId, placeId, post)
    }
    handleClose(true)
    setLoading(false)
  }

  const handleCreateSupplier = async (data) => {
    const res = await onCreateSupplier(data)
    if (res) {
      setIsCreateSupplierOpen(false)
    }
  }

  const handleCreateMaterial = async (data) => {
    const res = await onCreateMaterial(data)
    if (res) {
      setIsCreateMaterialOpen(false)
    }
  }

  return (
    <Dialog
      onClose={onClose}
      open={!!open}
      maxWidth={false}
      PaperProps={{ style: { maxWidth: 1080, width: '100%' } }}
      
    >
      <div className={classes.createExpense}>
        <div className={classes.formSection}>
          <div className={classes.formHeader}>
            <div className={classes.formTitle}>
              {
                form.id
                  ? t('Edit Expense')
                  : t('Add New Expense')
              }
            </div>
            <div className={classes.closeButton} onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
          <div className={classes.formRow}>
            <div className={classes.formItem}>
              <Label htmlFor='amount' text={<>{t('Amount')}<span className={classes.requiredMark}>*</span></>} />
              <Input
                id='amount'
                name='amount'
                type='number'
                placeholder={t('Enter amount')}
                value={form.amount}
                onChange={e => {
                  amountRef.current = e.target.value
                  handleInput(e)
                  setForm(prev => ({
                    ...prev,
                    materials: [{
                      material_id: '',
                      quantity: '1',
                      price: '0'
                    }]
                  }))
                }}
                error={errors.amount}
                fullWidth
              />
            </div>
            <div className={classes.formItem}>
              <Label text={<>{t('Currency')}<span className={classes.requiredMark}>*</span></>} htmlFor='currency_code' />
              <InputSelect
                id='currency_code'
                placeholder={t('Select currency')}
                options={currenciesOptions}
                value={form.currency_code}
                name='currency_code'
                onChange={handleInput}
                valueKey='id'
                labelKey='name'
              />
            </div>
          </div>
          <div className={classes.formRow}>
            <div className={classes.formItem}>
              <Label htmlFor='vendorsupplier_idname' text={<>{t('Supplier')}<span className={classes.requiredMark}>*</span></>} />
              <button type='button' className={classes.addButton} onClick={() => setIsCreateSupplierOpen(true)}>{t('+ Add Supplier')}</button>
              <InputSelect
                id='supplier_id'
                placeholder={t('Select currency')}
                options={suppliersOptions}
                value={form.supplier_id}
                name='supplier_id'
                onChange={handleInput}
                valueKey='id'
                labelKey='name'
              />
            </div>
            <div className={classes.formItem}>
              <Label htmlFor='invoiceNumber' text={t('Invoice Number')} />
              <Input
                id='invoiceNumber'
                name='invoiceNumber'
                placeholder={t('Enter invoice number')}
                value={form.invoiceNumber}
                onChange={handleInput}
                fullWidth
              />
            </div>
          </div>
          <div className={classes.formRow}>
            <div className={classes.formItem}>
              <Label htmlFor='invoiceDate' text={<>{t('Invoice Date')}<span className={classes.requiredMark}>*</span></>} />
              <Input
                id='invoiceDate'
                name='invoiceDate'
                type='date'
                placeholder={dateInputFormat}
                value={form.invoiceDate}
                onChange={handleInput}
                error={errors.invoiceDate}
                fullWidth
              />
            </div>
            <div className={classes.formItem}>
              <Label text={t('Assign to customer')} htmlFor='customer_id' />
              <InputSelect
                id='customer_id'
                placeholder={t('Select customer')}
                options={merchantsOptions}
                value={form.customer_id}
                name='customer_id'
                onChange={handleInput}
                valueKey='id'
                labelKey='name'
              />
            </div>
          </div>
          <div className={classes.formRow}>
            <div className={classes.formItem}>
              <Label text={t('Expenses Type')} htmlFor='expensesType' />
              <InputSelect
                id='expensesType'
                placeholder={t('Select expenses type')}
                options={expenseTypesOptions}
                value={form.expensesType}
                name='expensesType'
                onChange={handleInput}
                valueKey='id'
                labelKey='name'
              />
            </div>
            <div className={classes.formItem} />
          </div>
          <div className={classes.materials}>
            {
              form.materials.map((material, index) => {
                const isFirst = index === 0
                return (
                  <div className={classes.formRow} key={index}>
                    <div className={classes.formItem}>
                      {
                        isFirst
                          ? <Label text={t('Material')} htmlFor={`material_${index}`} />
                          : null
                      }
                      {
                        isFirst
                          ? <button type='button' className={classes.addButton} onClick={() => setIsCreateMaterialOpen(true)}>{t('+ Add material')}</button>
                          : null
                      }
                      <InputSelect
                        id={`material_${index}`}
                        placeholder={t('Select material')}
                        options={materials}
                        value={material.material_id}
                        name={`materials[${index}].material_id`}
                        onChange={e => {
                          const { value } = e.target
                          const material = materials.find(m => m.id === value)
                          setForm(prev => {
                            return {
                              ...prev,
                              materials: prev.materials.map((m, i) => i === index ? { ...m, material_id: value, price: material.price, unit: material.unit } : m),
                            }
                          })
                        }}
                        valueKey='id'
                        labelKey='name'
                      />
                    </div>
                    <div className={classes.formItem}>
                      {
                        isFirst
                          ? <Label text={t('Quantity')} htmlFor={`quantity_${index}`} />
                          : null
                      }
                      <div className={classes.quantityInput}>
                        <input
                          id={`quantity_${index}`}
                          name={`materials[${index}].quantity`}
                          type='number'
                          value={material.quantity}
                          onChange={e => {
                            const { value } = e.target
                            setForm(prev => {
                              return {
                                ...prev,
                                materials: prev.materials.map((m, i) => i === index ? { ...m, quantity: value } : m)
                              }
                            })
                          }}
                        />
                        <div className={classes.unit}>{material.unit}</div>
                      </div>
                    </div>
                    <div className={classes.formItem}>
                      {
                        isFirst
                          ? <Label text={t('Price')} htmlFor={`price_${index}`} />
                          : null
                      }
                      <Input
                        id={`price_${index}`}
                        name={`materials[${index}].price`}
                        type='number'
                        value={material.price}
                        onChange={e => {
                          const { value } = e.target
                          setForm(prev => {
                            return {
                              ...prev,
                              materials: prev.materials.map((m, i) => i === index ? { ...m, price: value } : m)
                            }
                          })
                        }}
                        fullWidth
                      />
                    </div>
                    <button type="button" className={classes.removeButton} onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        materials: prev.materials.filter((_, i) => i !== index)
                      }))
                    }}>
                      <TrashIcon />
                    </button>
                  </div>
                )
              })
            }
            <button type="button" className={classes.addMaterial} onClick={() => {
              setForm(prev => ({
                ...prev,
                materials: [...prev.materials, { material_id: '', quantity: '1', price: '', unit: '' }]
              }))
            }}>{t('Add material')}</button>
          </div>
          <div className={classes.formItem}>
            <Label htmlFor='notes' text={t('Notes')} />
            <textarea
              id='notes'
              name='notes'
              placeholder={t('Enter notes')}
              value={form.notes}
              onChange={handleInput}
              rows={4}
              className={classes.textarea}
            />
          </div>
          <Button
            style={{marginLeft: 'auto'}}
            size='big'
            loading={loading}
            onClick={handleSubmit}>
              {form.id ? t('Save edit') : t('Add Expense')}
          </Button>
        </div>
        {
          !form.id || (form.id && form.file)
            ? <div className={classes.fileSection}>
                {
                  !form.id
                    ? <div className={classes.detectAutomatically}>
                        <div>
                          <WandIcon />
                          <div>{t('Detect automatically')}</div>
                        </div>
                        <AppCheckbox
                          checked={form.detectAutomatically}
                          onChange={(value) => handleInput({target: {name: 'detectAutomatically', value}})}
                        />
                      </div>
                    : null
                }
                <div className={classes.detectionConfidence}>
                  {
                    form.detectAutomatically && form.file && !analyzing && !form.id
                      ? detectionConfidence > 80
                        ? <div className={classes.success}><CheckIcon />{t('Detected')} - {detectionConfidence}%</div>
                        : <div className={classes.failed}><AttentionIcon />{t('Detection failed — enter manually')}</div>
                      : null
                  }
                </div>
                <div
                  className={cn(classes.dropZone, {[classes.analyzing]: analyzing, [classes.error]: errors.file, [classes.edit]: form.id})}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => {
                    if (fileInputRef.current && !form.id) {
                      fileInputRef.current.click()
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    disabled={analyzing}
                    hidden
                    onChange={handleFileChange}
                    accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf" />
                  {
                    analyzing
                      ? <>
                          <RefreshIcon className={classes.refreshIcon} />
                          <div className={classes.fileName}>{t('Analyzing invoice')}</div>
                        </>
                      : !form.file
                        ? <>
                          <UploadIcon />
                          <div className={classes.fileName}>
                            {form.file ? form.file.name : t('Drag & Drop invoice or click to browse')}
                          </div>
                          <div className={classes.fileType}>PDF, PNG, JPEG/JPG, WEBP</div>
                        </>
                      : <div className={classes.preview}>
                          {
                            previewUrl
                              ? <ReactImageMagnify
                                enlargedImagePosition="over"
                                smallImage={{
                                  alt: 'Wristwatch by Ted Baker London',
                                  isFluidWidth: true,
                                  src: previewUrl
                                }}
                                largeImage={{
                                  src: previewUrl,
                                  width: 720 * 1,
                                  height: 720 * 1.414
                                }} />
                              : null
                            
                          }
                        </div>
                  }
                </div>
              </div>
            : null
        }
        {/* {
          rawDetectedData
            ? <div style={{width: '100%', flex: 1}}><JSONTree data={rawDetectedData} /></div>
            : null
        } */}
      </div>
      <CreateSupplier
        open={isCreateSupplierOpen}
        handleClose={() => setIsCreateSupplierOpen(false)}
        buttonTitle={t('Create supplier')}
        saveSupplier={handleCreateSupplier}
      />
      <CreateMaterial
        open={isCreateMaterialOpen}
        handleClose={() => setIsCreateMaterialOpen(false)}
        buttonTitle={t('Create material')}
        saveMaterial={handleCreateMaterial}
      />
    </Dialog>
  )
})

CreateExpense.displayName = 'CreateExpense'

export default CreateExpense
